// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import type { ConnectorCallbackConnector } from "../../../utils/connectorCallback";

export const CONNECTOR_DISPLAY_NAMES: Record<ConnectorCallbackConnector, string> = {
  google: "Google Workspace",
  graph: "Microsoft 365",
  docusign: "DocuSign",
};

export const CONNECT_AGAIN_LABEL = "Connect Again";

export const REFRESH_LABEL = "Refresh";

export const TAKEOVER_LABEL = "Move It Here";

export type ProviderErrorAction = "connect-again" | "refresh" | "takeover" | null;

export interface ProviderErrorMessage {
  title: string;
  body: string;
  intent: "error" | "warning" | "info";
  action: ProviderErrorAction;
}

const CONSENT_REQUIRED_BODIES: Record<ConnectorCallbackConnector, string> = {
  google:
    "Your organization requires an administrator to approve Riddle. Ask your IT admin to consent, then try again.",
  graph:
    "Your organization requires an administrator to approve Riddle. Ask your IT admin to consent, then try again.",
  docusign:
    "DocuSign requires the account administrator to approve Riddle. Ask your DocuSign admin to grant consent, then try again.",
};

function errorBody(connector: ConnectorCallbackConnector, reason: string | undefined): string {
  const name = CONNECTOR_DISPLAY_NAMES[connector];
  switch (reason) {
    case "sign-in-window-closed":
      return `The ${name} sign-in window was closed, so nothing was connected.`;
    case "sign-in-timed-out":
      return `The ${name} sign-in took too long, so nothing was connected.`;
    case "connection_conflict":
      return `${name} was connected or changed in another window. Refresh to see the current connection.`;
    case "access_denied":
      return `You canceled the ${name} sign-in, so nothing was shared with Riddle.`;
    case "state_invalid":
      return "This sign-in link expired or was already used. Connect again from here.";
    case "account_already_linked":
      return `This ${name} account is already connected to another Riddle workspace. Move it here, or connect a different account.`;
    case "permission_denied":
      return `You don't have permission to connect ${name}. Ask a workspace admin.`;
    case "consent_required":
      return CONSENT_REQUIRED_BODIES[connector];
    case "graph_account_unsupported":
      return "This Microsoft account has no Outlook/Exchange mailbox. Sign in with your work or school account.";
    case "graph_access_refused":
      return "Microsoft 365 refused access for this account. Ask your IT admin to check its access policies, then try again.";
    default:
      return `We couldn't connect ${name}. Try again.`;
  }
}

function errorIntent(reason: string | undefined): ProviderErrorMessage["intent"] {
  switch (reason) {
    case "sign-in-window-closed":
      return "info";
    case "sign-in-timed-out":
      return "warning";
    default:
      return "error";
  }
}

function errorAction(reason: string | undefined): ProviderErrorAction {
  switch (reason) {
    case "permission_denied":
      return null;
    case "connection_conflict":
      return "refresh";
    case "account_already_linked":
      return "takeover";
    default:
      return "connect-again";
  }
}

export function providerConnectedMessage(connector: ConnectorCallbackConnector): string {
  return `${CONNECTOR_DISPLAY_NAMES[connector]} is connected.`;
}

export function providerNotConnectedTitle(connector: ConnectorCallbackConnector): string {
  return `${CONNECTOR_DISPLAY_NAMES[connector]} wasn't connected`;
}

export function toProviderErrorMessage(
  connector: ConnectorCallbackConnector,
  reason: string | undefined,
): ProviderErrorMessage {
  return {
    title: providerNotConnectedTitle(connector),
    body: errorBody(connector, reason),
    intent: errorIntent(reason),
    action: errorAction(reason),
  };
}
